// ============================================================
// ЗАДАЧА 1: Парсеры источников данных
// ============================================================

// ---------- Telegram HTML ----------

export function parseTelegramHTML(htmlFiles) {
  const messages = []
  const participantCounts = {}
  const participantChats = {}   // name → Set of chat names
  const participantChatTypes = {} // name → Set of chatTypes
  let personalChats = 0
  let groupChats = 0

  for (const file of htmlFiles) {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(file.content, 'text/html')

      // Название чата
      const chatName =
        doc.querySelector('.page_header .text')?.textContent?.trim() ||
        doc.querySelector('h4')?.textContent?.trim() ||
        file.name.replace(/\.html?$/i, '')

      // Определяем тип чата по уникальным отправителям
      const fromNames = new Set()
      doc.querySelectorAll('.message.default .from_name').forEach((el) => {
        const name = el.textContent.trim()
        if (name) fromNames.add(name)
      })
      // > 1 уникальных отправителей с меткой → группа; в личных чатах .from_name есть только у собеседника
      const chatType = fromNames.size > 1 ? 'group' : 'personal'
      if (chatType === 'group') groupChats++
      else personalChats++

      const messageDivs = doc.querySelectorAll('.message.default, .message.service')

      for (const msgDiv of messageDivs) {
        if (msgDiv.classList.contains('service')) continue

        const isOwn = msgDiv.classList.contains('own')
        const fromEl = msgDiv.querySelector('.from_name')
        const fromName = fromEl?.textContent?.trim()
        // own без имени → __self__; чужое в личном чате без имени → chatName (имя чата = имя собеседника)
        const from = fromName || (isOwn ? '__self__' : (chatType === 'personal' ? chatName : ''))
        const fromId = msgDiv.id?.replace('message', '') || ''

        const textEl = msgDiv.querySelector('.text')
        const text = textEl?.textContent?.trim() || ''

        const dateEl = msgDiv.querySelector('.date.details')
        const date =
          dateEl?.getAttribute('title') || dateEl?.textContent?.trim() || ''

        // Контекст — текст сообщения на которое отвечают
        const replyEl =
          msgDiv.querySelector('.reply_to .text') ||
          msgDiv.querySelector('.reply_to .reply_media_content')
        const reply_to_text = replyEl?.textContent?.trim() || null

        if (from && text) {
          messages.push({
            from,
            from_id: fromId,
            text,
            date,
            chatName,
            chatType,
            reply_to_text,
          })
          participantCounts[from] = (participantCounts[from] || 0) + 1
          if (!participantChats[from]) participantChats[from] = new Set()
          participantChats[from].add(chatName)
          if (!participantChatTypes[from]) participantChatTypes[from] = new Set()
          participantChatTypes[from].add(chatType)
        }
      }

      console.log(
        `[PARSER] Telegram: ${file.name} → ${chatType}, ${messages.length} msgs total`
      )
    } catch (e) {
      console.error(`[PARSER] Telegram error in ${file.name}:`, e)
    }
  }

  const participants = Object.entries(participantCounts)
    .map(([name, count]) => ({
      name,
      count,
      chats: [...(participantChats[name] || [])],
      chatTypes: [...(participantChatTypes[name] || [])],
    }))
    .sort((a, b) => b.count - a.count)

  console.log(
    `[PARSER] Telegram total: ${messages.length} msgs, ${participants.length} participants, personal:${personalChats} group:${groupChats}`
  )

  return { messages, participants, personalChats, groupChats }
}

// ---------- Apple Journal HTML ----------

export function parseJournalHTML(htmlFiles) {
  const entries = []

  for (const file of htmlFiles) {
    try {
      // Дата из имени файла (форматы: 2024-01-15, 15.01.2024, Jan 15 2024)
      let date = ''
      const isoMatch = file.name.match(/(\d{4}-\d{2}-\d{2})/)
      const dotMatch = file.name.match(/(\d{2}\.\d{2}\.\d{4})/)
      if (isoMatch) date = isoMatch[1]
      else if (dotMatch) {
        const parts = dotMatch[1].split('.')
        date = `${parts[2]}-${parts[1]}-${parts[0]}`
      }

      const parser = new DOMParser()
      const doc = parser.parseFromString(file.content, 'text/html')

      // Дата из контента, если не нашли в имени файла
      if (!date) {
        const dateEl =
          doc.querySelector('time') ||
          doc.querySelector('.date') ||
          doc.querySelector('header')
        if (dateEl) date = dateEl.textContent.trim()
      }

      // Текст записи — всё содержимое body без навигации
      const body = doc.querySelector('article') || doc.querySelector('main') || doc.body
      ;['.nav', 'nav', 'header', 'footer'].forEach((sel) =>
        body?.querySelector(sel)?.remove()
      )
      const text = body?.textContent?.trim().replace(/\s+/g, ' ') || ''

      if (text) {
        entries.push({ date, text, filename: file.name })
        console.log(`[PARSER] Journal: ${file.name} → ${date}, ${text.length} chars`)
      }
    } catch (e) {
      console.error(`[PARSER] Journal error in ${file.name}:`, e)
    }
  }

  console.log(`[PARSER] Journal total: ${entries.length} entries`)
  return entries
}

// ---------- Резюме (.docx / .txt) ----------

export async function parseResume(file) {
  try {
    const name = file.name.toLowerCase()

    if (name.endsWith('.docx')) {
      const mammoth = await import('mammoth')
      const arrayBuffer = await file.arrayBuffer()
      const result = await mammoth.extractRawText({ arrayBuffer })
      const text = result.value.trim()
      console.log(`[PARSER] Resume .docx: ${text.length} chars`)
      return text
    }

    if (name.endsWith('.txt') || name.endsWith('.md')) {
      const text = await file.text()
      console.log(`[PARSER] Resume .txt: ${text.length} chars`)
      return text.trim()
    }

    throw new Error(`Unsupported resume format: ${file.name}`)
  } catch (e) {
    console.error('[PARSER] Resume error:', e)
    throw e
  }
}

// ---------- Threads JSON (Meta Export) ----------

// Исправляет Mojibake: UTF-8 байты прочитанные как Latin-1
function fixMojibake(str) {
  if (!str) return str
  try {
    const codes = [...str].map(c => c.charCodeAt(0))
    if (codes.every(c => c <= 255)) {
      return new TextDecoder('utf-8').decode(new Uint8Array(codes))
    }
  } catch {}
  return str
}

export function parseThreadsExport(jsonFiles) {
  const posts = []
  const replies = []
  const dm = []
  const dmParticipantsMap = {}

  for (const file of jsonFiles) {
    try {
      const data = JSON.parse(file.content)

      // Debug: логируем ключи файла чтобы понять формат
      console.log(`[PARSER] Threads file "${file.name}" top-level keys:`, Object.keys(data), {
        has_text_post_app_text_posts: Array.isArray(data.text_post_app_text_posts),
        has_messages: Array.isArray(data.messages),
        has_direct_messages: Array.isArray(data.direct_messages),
        has_participants: Array.isArray(data.participants),
        messages_count: data.messages?.length,
        participants_count: data.participants?.length,
        first_msg_keys: data.messages?.[0] ? Object.keys(data.messages[0]) : null,
      })

      // Формат выгрузки 2024-2026: threads_and_replies.json
      if (Array.isArray(data.text_post_app_text_posts)) {
        for (const item of data.text_post_app_text_posts) {
          const media = item.media?.[0] || {}
          const raw = media.title || ''
          const text = fixMojibake(raw).trim()
          if (!text) continue
          const date = media.creation_timestamp || ''
          const isReply = !!media.text_app_post?.is_reply
          if (isReply) {
            replies.push({ text, date })
          } else {
            posts.push({ text, date })
          }
        }
      }

      // Старый формат: organic_insights_threads
      if (data.organic_insights_threads) {
        for (const item of data.organic_insights_threads) {
          const text = fixMojibake(
            item.string_map_data?.['Post']?.value ||
            item.string_map_data?.['Text']?.value ||
            item.title || ''
          ).trim()
          if (text) posts.push({ text, date: item.timestamp || '' })
        }
      }

      // Старый формат: threads_v2
      if (Array.isArray(data.threads_v2)) {
        for (const thread of data.threads_v2) {
          for (const item of thread.post?.thread_items || []) {
            const text = fixMojibake(
              item.post?.caption?.text || item.post?.text?.text || ''
            ).trim()
            if (text) {
              const isReply = !!item.post?.original_media_owner
              const date = item.post?.taken_at || ''
              if (isReply) replies.push({ text, date })
              else posts.push({ text, date })
            }
          }
        }
      }

      // direct_messages (в некоторых выгрузках)
      if (data.direct_messages || Array.isArray(data.messages)) {
        const msgs = data.direct_messages || data.messages
        if (Array.isArray(data.participants)) {
          for (const p of data.participants) {
            const name = fixMojibake(p.name || p.username || '').trim()
            if (name) {
              if (!dmParticipantsMap[name]) dmParticipantsMap[name] = { count: 0, conversations: new Set() }
              dmParticipantsMap[name].conversations.add(file.name)
            }
          }
        }
        for (const msg of msgs) {
          const text = fixMojibake(msg.text || msg.content || msg.body || '').trim()
          const sender = fixMojibake(msg.sender_name || msg.sender || '').trim()
          const date = msg.timestamp_ms ? String(Math.floor(msg.timestamp_ms / 1000)) : (msg.timestamp || msg.date || '')
          if (text) dm.push({ text, date, from: sender, chatType: 'threads_dm' })
          if (sender) {
            if (!dmParticipantsMap[sender]) dmParticipantsMap[sender] = { count: 0, conversations: new Set() }
            dmParticipantsMap[sender].count++
            dmParticipantsMap[sender].conversations.add(file.name)
          }
        }
      }

      console.log(
        `[PARSER] Threads: ${file.name} → posts:${posts.length} replies:${replies.length} dm:${dm.length}`
      )
    } catch (e) {
      console.error(`[PARSER] Threads error in ${file.name}:`, e)
    }
  }

  const dmParticipants = Object.entries(dmParticipantsMap).map(([name, data]) => ({
    name,
    count: data.count,
    conversations: [...data.conversations],
  }))

  console.log(
    `[PARSER] Threads total: posts:${posts.length} replies:${replies.length} dm:${dm.length} dmParticipants:${dmParticipants.length}`
  )
  return { posts, replies, dm, dmParticipants }
}

// ---------- Утилита: читаем File → {name, content} ----------

export async function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve({ name: file.name, content: e.target.result })
    reader.onerror = () => reject(new Error(`Cannot read ${file.name}`))
    reader.readAsText(file, 'utf-8')
  })
}

// ---------- Резюме → чанки без потери текста ----------
// Резюме — один документ: режем по абзацам на куски до maxLen, ничего не отбрасывая.

export function chunkResumeText(text, maxLen = 3000) {
  const clean = (text || '').replace(/\r\n/g, '\n').trim()
  if (!clean) return []
  const chunks = []
  let cur = ''
  const push = () => { if (cur.trim()) chunks.push(cur.trim()); cur = '' }
  for (const para of clean.split(/\n\s*\n/)) {
    if (para.length > maxLen) {
      push()
      for (let i = 0; i < para.length; i += maxLen) chunks.push(para.slice(i, i + maxLen).trim())
      continue
    }
    if (cur && cur.length + para.length + 2 > maxLen) push()
    cur += (cur ? '\n\n' : '') + para
  }
  push()
  return chunks
}
